import type {
  BindingContextDef,
  RootBindingContextDef,
  PageBindingContextDef,
  ListBindingContextDef,
  EntityBindingContextDef,
} from '@packages/systems/dynamo/source-types';
import type {
  RootTypeReference,
  EntityTypeReference,
  ListTypeReference,
} from './type';

export type ContextIdentifier = string;

type BindingContextDefinition = BindingContextDef<
  RootTypeReference,
  EntityTypeReference,
  ListTypeReference
>;

/** Internal types */

export type RootBindingContext = RootBindingContextDef<RootTypeReference>;
export type PageBindingContext = PageBindingContextDef<EntityTypeReference>;
export type ListBindingContext = ListBindingContextDef<ListTypeReference>;
export type EntityBindingContext = EntityBindingContextDef<EntityTypeReference>;

export type BindingContext =
  | RootBindingContext
  | PageBindingContext
  | ListBindingContext
  | EntityBindingContext;

/** Constructor for internal type */

export function buildBindingContext(
  definition: BindingContextDefinition
): BindingContext {
  if (definition.variant === 'List' && definition.connection) {
    // We make connections immutable so that connections can be compared to
    // connection options using referential equality. As we make the references
    // to the connections available to consumers through the connection options,
    // we don't want the consumers to be able to mutate the connections.
    Object.freeze(definition.connection);
  }
  return definition;
}

/** Utilities for handling internal types */

export function getTypeReference(bindingContext: BindingContext) {
  return bindingContext.type;
}

type ContextMapping = {
  readonly root?: RootBindingContext;
  readonly elements?: Partial<
    Record<ContextIdentifier, ListBindingContext | EntityBindingContext>
  >;
  readonly pages?: Partial<Record<ContextIdentifier, PageBindingContext>>;
};

export function buildContextMapping(
  definitions: BindingContextDefinition[]
): ContextMapping {
  return definitions.reduce<{
    root?: ContextMapping['root'];
    elements?: ContextMapping['elements'];
    pages?: ContextMapping['pages'];
  }>((mapping, definition) => {
    const bindingContext = buildBindingContext(definition);

    if (bindingContext.variant === 'Root') {
      mapping.root = bindingContext;
    } else if (bindingContext.variant === 'Page') {
      if (mapping.pages == null) {
        mapping.pages = {};
      }
      mapping.pages[bindingContext.id] = bindingContext;
    } else {
      // List and Entity binding context variants
      if (mapping.elements == null) {
        mapping.elements = {};
      }
      mapping.elements[bindingContext.id] = bindingContext;
    }

    return mapping;
  }, {});
}
